import { Button, Grid, Group, MultiSelect, noop, Radio, Select, Stack, Textarea, TextInput } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useHotkeys } from '@mantine/hooks'
import type { AssetGroup, AssetSchema, AssetTracker } from '@opsopti/shared'
import {
  assetSchema,
  castAssetToForm,
  isFieldsListValid,
  useAssetCreateMutation,
  useAssetForm,
  useAssetGroupsQuery,
  useAssetQuery,
  useAssetTrackersQuery,
  useAssetUpdateMutation,
  useGlobalState,
} from '@opsopti/shared'
import type { CreateAssetResponse, UpdateAssetResponse } from '@opsopti/shared/src/services/api/api.types'
import { useAreFormQueriesFetched } from '@opsopti/shared/src/utils/tanstackHelpers'
import { IconFolder, IconScan } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { createContext, Fragment, useContext, useEffect, useMemo } from 'react'
import type { z } from 'zod'

import { RadioCard } from '@/components/RadioCard'
import { Screen, type ScreenProps } from '@/components/Screen'
import { SectionedForm, type SectionedFormProps } from '@/components/SectionedForm'
import type { RouteParams } from '@/configs/routes'
import {
  getFormScreenTitle,
  getFormSubmitLabel,
  showErrorNotification,
  showSuccessNotification,
} from '@/utils/componentCommons'
import { goBack } from '@/utils/navigation'

import { AssetSentryGroupFormScreen } from './AssetSentryGroupFormScreen'
import { AssetSentryTrackerFormScreen } from './AssetSentryTrackerFormScreen'
import { TableScreenDescription, tableScreenIcon } from './TableScreen'

type AssetSentryAssetFormScreenProps = {
  params?: RouteParams['assetSentryAssetNew'] | RouteParams['assetSentryAssetEdit']
  additionalParams?: undefined | { assetGroupId?: string | null }
  type?: 'screen' | 'drawer'
  goBackFn?: () => void
  onAfterSubmitFn?: (asset: CreateAssetResponse | UpdateAssetResponse) => void
}

type FormSection = {
  id: string | null
  type: 'screen' | 'drawer'
  form: UseFormReturnType<z.infer<AssetSchema>>
  groups: AssetGroup[]
  trackers: AssetTracker[]
  isMutating: boolean
  submitFn: () => Promise<void>
  goBackFn: () => void
}

const AssetFormDataContext = createContext<FormSection>({} as FormSection)
const useAssetFormData = () => useContext(AssetFormDataContext)

export const AssetSentryAssetFormScreen = observer(function AssetSentryAssetFormScreen(
  props: AssetSentryAssetFormScreenProps,
) {
  const { uiStore } = useGlobalState()

  const id = props.params?.id ?? null
  const isNew = useMemo(() => !id, [id])
  const type = props.type ?? 'screen'
  const { assetGroupId = null } = props.additionalParams ?? {}

  const { form } = useAssetForm()

  const assetQuery = useAssetQuery({ params: { id } })
  const assetGroupsQuery = useAssetGroupsQuery({ params: { depth: '0' } })
  const assetTrackersQuery = useAssetTrackersQuery({ params: { depth: '0' } })
  const assetCreateMutation = useAssetCreateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })
  const assetUpdateMutation = useAssetUpdateMutation({
    onErrorNotification: showErrorNotification,
    onSuccessNotification: showSuccessNotification,
  })

  const areQueriesLoaded = useAreFormQueriesFetched(
    [
      !isNew ? assetQuery.isFetched : null,
      !!assetGroupId ? assetGroupsQuery.isFetched : null,
      assetGroupsQuery.isFetched,
      assetTrackersQuery.isFetched,
    ],
    200,
  )

  useEffect(() => {
    if (areQueriesLoaded) {
      form.initialize(castAssetToForm(assetQuery.data))
      if (assetGroupId) {
        form.setFieldValue('assetGroup', assetGroupId)
      }
      uiStore.hideLoadingOverlay()
    } else {
      uiStore.showLoadingOverlay()
    }
  }, [assetGroupId, areQueriesLoaded])

  useEffect(() => uiStore.hideLoadingOverlay, [])

  const isSubDrawerOpen = !!uiStore.formDrawers.find(d => d.id === 'new-group-drawer' || d.id === 'new-tracker-drawer')

  useHotkeys([['mod+S', isSubDrawerOpen ? noop : submitFn]], [])

  const goBackFn = props.goBackFn ?? goBack

  async function submitFn() {
    const validations = form.validate()

    if (validations.hasErrors) {
      showErrorNotification({ title: 'Form Errors', message: 'Please fill out all required fields before submitting.' })
      return
    }

    let response: CreateAssetResponse | UpdateAssetResponse | undefined

    if (isNew) {
      response = await assetCreateMutation.mutateAsync(form.getValues())
    } else {
      response = await assetUpdateMutation.mutateAsync({ ...form.getValues(), id: id! })
    }

    props.onAfterSubmitFn?.(response)

    goBackFn()
  }

  const sections = useMemo(
    () =>
      [
        {
          id: 'general',
          title: 'General Details',
          subtitle: 'Basic details about the asset.',
          collapsible: false,
          valid: isFieldsListValid(form, ['name', 'identifier', 'assetGroup']),
          ContentComponent: GeneralFields,
        },
        {
          id: 'additional',
          title: 'Additional Information',
          subtitle: 'Visibility settings, notes, and other information.',
          collapsible: false,
          valid: isFieldsListValid(form, ['visibilityStatus']),
          ContentComponent: AdditionalFields,
        },
        {
          id: 'actions',
          collapsible: false,
          ContentComponent: FormActions,
        },
      ] satisfies SectionedFormProps['sections'],
    [form],
  )

  const isMutating = assetCreateMutation.isPending || assetUpdateMutation.isPending
  const FormWrapper = type === 'screen' ? Screen : Fragment
  const FormWrapperProps: ScreenProps =
    type === 'screen'
      ? {
          maw: 1440,
          title: getFormScreenTitle(id, 'Asset', assetQuery.data?.name),
          icon: tableScreenIcon['assets'],
          description: <TableScreenDescription entity="assets" />,
        }
      : ({} as ScreenProps)

  return (
    <FormWrapper {...FormWrapperProps}>
      <AssetFormDataContext.Provider
        value={{
          id,
          form,
          type,
          submitFn,
          goBackFn,
          isMutating,
          groups: assetGroupsQuery.data?.docs ?? [],
          trackers: assetTrackersQuery.data?.docs ?? [],
        }}>
        <SectionedForm sections={sections} sectionsWithBorder={type === 'screen'} />
      </AssetFormDataContext.Provider>
    </FormWrapper>
  )
})

function GeneralFields() {
  const props = useAssetFormData()
  const { uiStore } = useGlobalState()

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <TextInput
          withAsterisk
          label="Name"
          description="A unique label for the asset."
          {...props.form.getInputProps('name')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <TextInput
          withAsterisk
          label="Identifier"
          description="An optional internal identifier for the asset (e.g. serial number, phone number, etc)."
          {...props.form.getInputProps('identifier')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Group align="center">
          <Select
            withAsterisk
            clearable
            searchable
            label="Group"
            placeholder="Select a group..."
            description="The group that the Asset belongs to."
            data={props.groups.map(g => ({ value: g.id, label: g.name }))}
            flex={'1 1 auto'}
            {...props.form.getInputProps('assetGroup')}
          />

          <Button
            size="sm"
            pos="relative"
            top={3}
            leftSection={<IconFolder size={22} />}
            onClick={() =>
              uiStore.openFormDrawer({
                id: 'new-group-drawer',
                Component: AssetSentryGroupFormScreen,
                ComponentProps: {
                  type: 'drawer',
                  goBackFn: uiStore.closeFormDrawer,
                  onAfterSubmitFn: group => props.form.setFieldValue('assetGroup', group.doc.id),
                },
                TitleProps: {
                  Icon: tableScreenIcon['groups'],
                  title: getFormScreenTitle(null, 'Group'),
                },
              })
            }>
            Add New Group
          </Button>
        </Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Group align="center">
          <MultiSelect
            searchable
            clearable
            label="Trackers"
            placeholder="Select one or more trackers..."
            description="The trackers that are attached to this asset."
            data={props.trackers
              .filter(t => !t.asset?.docs?.length || props.form.getValues().assetTrackers.includes(t.id))
              .map(t => {
                let label = t.typeWithIdentifier ?? t.identifier

                if (props.type === 'drawer') {
                  label = label.length > 30 ? label.slice(0, 30) + '...' : label
                }

                return { value: t.id, label }
              })}
            {...props.form.getInputProps('assetTrackers')}
            flex={'1 1 auto'}
          />

          <Button
            size="sm"
            pos="relative"
            top={3}
            leftSection={<IconScan size={22} />}
            onClick={() =>
              uiStore.openFormDrawer({
                id: 'new-tracker-drawer',
                Component: AssetSentryTrackerFormScreen,
                ComponentProps: {
                  goBackFn: uiStore.closeFormDrawer,
                  type: 'drawer',
                  onAfterSubmitFn: tracker => props.form.insertListItem('assetTrackers', tracker.doc.id),
                },
                TitleProps: {
                  Icon: tableScreenIcon['trackers'],
                  title: getFormScreenTitle(null, 'Tracker'),
                },
              })
            }>
            Add New Tracker
          </Button>
        </Group>
      </Grid.Col>
    </Grid>
  )
}

function AdditionalFields() {
  const props = useAssetFormData()

  const RadioWrapper = props.type === 'screen' ? Group : Stack
  const RadioComponent = props.type === 'screen' ? RadioCard : Radio

  return (
    <Grid gutter={props.type === 'screen' ? 'xxl' : 'xl'}>
      <Grid.Col span={12}>
        <Radio.Group label="Visibility" withAsterisk {...props.form.getInputProps('visibilityStatus')}>
          <RadioWrapper align="stretch">
            <RadioComponent
              value={assetSchema.shape.visibilityStatus.enum.active}
              label="Active"
              description="Asset is available for use."
            />

            <RadioComponent
              value={assetSchema.shape.visibilityStatus.enum.inactive}
              label="Inactive"
              description="Asset is disabled and not available for assignment."
            />

            <RadioComponent
              value={assetSchema.shape.visibilityStatus.enum.archived}
              label="Archived"
              description="This asset is no longer in use."
            />
          </RadioWrapper>
        </Radio.Group>
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Supervisors"
          autosize
          minRows={4}
          description="Notes that can be used for internal purposes. Only visible to Supervisors."
          {...props.form.getInputProps('internalNotes')}
        />
      </Grid.Col>

      <Grid.Col span={12}>
        <Textarea
          label="Notes For Members"
          autosize
          minRows={4}
          description="Notes displayed publicly to Members."
          {...props.form.getInputProps('memberNotes')}
        />
      </Grid.Col>
    </Grid>
  )
}
function FormActions() {
  const props = useAssetFormData()

  return (
    <Stack gap="xxl">
      <Group gap="xl">
        <Button size="lg" radius="sm" onClick={props.submitFn} loading={props.isMutating}>
          {getFormSubmitLabel(props.id, 'Asset')}
        </Button>

        {!props.isMutating && (
          <Button size="sm" radius="sm" variant="light" color="gray" onClick={props.goBackFn}>
            Cancel
          </Button>
        )}
      </Group>
    </Stack>
  )
}
